import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
} from '@mui/material';
import {
  People,
  LocationOn,
  Route,
  TrendingUp,
  Speed,
  Security,
} from '@mui/icons-material';

const StatsSection: React.FC = () => {
  const stats = [
    {
      label: 'Active Users',
      value: '50K+',
      icon: <People />,
      color: '#667eea',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      description: 'Daily active users',
    },
    {
      label: 'Cities Covered',
      value: '25+',
      icon: <LocationOn />,
      color: '#4CAF50',
      gradient: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
      description: 'Metro networks',
    },
    {
      label: 'Routes Planned',
      value: '1M+',
      icon: <Route />,
      color: '#FF9800',
      gradient: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
      description: 'Optimized routes',
    },
    {
      label: 'Uptime',
      value: '99.9%',
      icon: <TrendingUp />,
      color: '#E91E63',
      gradient: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)',
      description: 'Service reliability',
    },
  ];

  return (
    <Box sx={{ py: 12, bgcolor: '#1a1a2e' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            label="Statistics"
            sx={{
              bgcolor: 'rgba(102, 126, 234, 0.2)',
              color: '#667eea',
              fontWeight: 600,
              mb: 2,
              border: '1px solid rgba(102, 126, 234, 0.3)',
            }}
          />
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              color: 'white',
            }}
          >
            Trusted by Millions
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              fontWeight: 400,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Join thousands of users who rely on MetroRahi for their daily commute
          </Typography>
        </Box>

        {/* Stats Grid */}
        <Grid container spacing={4}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease',
                  borderRadius: 4,
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  bgcolor: '#0f0f23',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                    '& .stat-icon': {
                      transform: 'scale(1.1)',
                    },
                  },
                  overflow: 'visible',
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  {/* Icon */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <Avatar
                      className="stat-icon"
                      sx={{
                        width: 70,
                        height: 70,
                        background: stat.gradient,
                        transition: 'transform 0.3s ease',
                        boxShadow: `0 8px 25px ${stat.color}40`,
                      }}
                    >
                      {stat.icon}
                    </Avatar>
                  </Box>

                  {/* Value */}
                  <Typography
                    variant="h2"
                    component="div"
                    sx={{
                      fontWeight: 800,
                      color: stat.color,
                      mb: 1,
                      fontSize: { xs: '2.5rem', md: '3rem' },
                    }}
                  >
                    {stat.value}
                  </Typography>

                  {/* Label */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'white',
                      mb: 1,
                    }}
                  >
                    {stat.label}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      opacity: 0.8,
                    }}
                  >
                    {stat.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Additional Info */}
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: '#4CAF50', mr: 2 }}>
                  <Speed />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                  Fast & Reliable
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Sub-second response times for route planning
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: '#FF9800', mr: 2 }}>
                  <Security />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                  Secure & Private
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Enterprise-grade security and privacy protection
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default StatsSection; 